/**
 * Mise à la corbeille d'une fiche entreprise — administrateur de PLATEFORME.
 *
 * Rien n'est effacé tout de suite : la fiche et ses données liées partent dans
 * la corbeille de l'organisation (pages/Trash.jsx), d'où elles se restaurent à
 * l'identique pendant TRASH_RETENTION_DAYS jours. Le serveur refuse hors
 * administrateur de plateforme (403) : la modale ne fait qu'expliquer le geste.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Trash2 } from "lucide-react";
import api, { apiError } from "@/lib/crmApi";
import Modal from "@/crm/components/Modal";

export const TRASH_RETENTION_DAYS = 30;

export default function TrashCompanyModal({ company, onClose, onDone }) {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const confirmer = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/trash/companies/${company.id}`);
      // Pas d'entrée de menu pour la corbeille : le toast en est le raccourci.
      toast.success(`« ${company.nom} » mise à la corbeille (${data.total} document(s) liés)`, {
        action: { label: "Voir la corbeille", onClick: () => navigate("/corbeille") },
      });
      onDone?.(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Suppression impossible"); }
    finally { setLoading(false); }
  };

  return (
    <Modal onClose={onClose} testid="trash-company-modal" label="Mettre la fiche à la corbeille" className="max-w-[520px]">
      <div className="flex items-center gap-3 mb-3 text-rose-600">
        <div className="h-11 w-11 rounded-full bg-rose-50 flex items-center justify-center"><Trash2 size={22} /></div>
        <h3 className="text-base font-semibold text-slate-800">Supprimer « {company.nom} » ?</h3>
      </div>
      <p className="text-sm text-slate-600">
        La fiche et tout ce qui s'y rattache (contacts, activités, notes, tâches, emails,
        appels, sessions Copilot) sont déplacés dans la corbeille. Pendant {TRASH_RETENTION_DAYS} jours,
        vous pouvez la restaurer à l'identique depuis Administration › Corbeille. Passé ce délai,
        elle est supprimée définitivement.
      </p>
      <p className="mt-2 flex items-start gap-1.5 text-xs text-slate-500">
        <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-500" />
        Les oppositions « Ne plus contacter » liées à cette entreprise sont conservées.
      </p>
      <div className="mt-5 flex justify-end gap-2">
        <button type="button" onClick={onClose} disabled={loading}
          className="rounded-lg border border-slate-300 px-3.5 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50">
          Annuler
        </button>
        <button type="button" data-testid="trash-company-confirm" onClick={confirmer} disabled={loading}
          className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-3.5 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50">
          <Trash2 size={15} /> Mettre à la corbeille
        </button>
      </div>
    </Modal>
  );
}
